import * as Sentry from '@sentry/vue'
import AiInsightsApi, { AI_INSIGHTS_TIMEOUT_MS, hasAiInsightsV2 } from '@/api/aiInsights'
import UnleashService from '@/services/unleash'
import { trackAIInsights } from '@/business-logic/ai-tracking'

const FETCH_AI_INSIGHTS_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

const moduleState = {
  insights: [],
  status: FETCH_AI_INSIGHTS_STATUS.LOADING,
}

const getters = {
  insights: (state) => state.insights,
  status: (state) => state.status,
}

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_INSIGHTS(state, insights) {
    state.insights = insights
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  /* eslint-enable no-param-reassign */
}

const actions = {
  async fetchAIInsightsAction({ commit, rootState }, { presentationId, sessionId, forceRefresh = false }) {
    const userId = rootState.currentUser?.id
    const lang = rootState.presenterLocale || 'en'
    const storageKey = `${userId}-${presentationId}-${sessionId}-${lang}-genai-analysis`

    if (!forceRefresh) {
      const cachedInsights = localStorage.getItem(storageKey)
      if (cachedInsights) {
        try {
          const insightsArray = JSON.parse(cachedInsights)
          commit('SET_INSIGHTS', insightsArray)
          commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)
          return
        } catch (e) {
          localStorage.removeItem(storageKey)
        }
      }
    }

    const startedAt = Date.now()
    try {
      trackAIInsights({ stage: 'started' })

      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.LOADING)
      const useAhaaiv2 = hasAiInsightsV2
        && UnleashService.getVariant('AHA-42863-ahaaiv2-routing').name === 'treatment'
      const body = await AiInsightsApi.getInsights({
        presentationId,
        sessionId,
        useV2: useAhaaiv2,
      })

      const insightsArray = body?.insights || []
      commit('SET_INSIGHTS', insightsArray)
      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)

      trackAIInsights({ stage: 'succeeded' })

      // Store in local storage
      if (insightsArray.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(insightsArray))
      }
    } catch (error) {
      // vue-resource reports status 0 for an aborted timeout and for every transport failure alike, so
      // only the elapsed time separates them.
      const isTimeout = error.status === 0 && Date.now() - startedAt >= AI_INSIGHTS_TIMEOUT_MS
      let reportedError = error
      if (isTimeout) {
        reportedError = new Error(`AI insights request timed out after ${AI_INSIGHTS_TIMEOUT_MS / 1000}s`)
      } else if (!(error instanceof Error)) {
        reportedError = new Error(`AI insights request failed with status ${error?.status ?? 'unknown'}`)
      }

      trackAIInsights({ stage: 'failed', error: reportedError })

      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.ERROR)
      Sentry.captureException(reportedError, {
        tags: {
          feature: 'ai-insights',
          context: 'fetch-ai-insights-action',
          isTimeout: isTimeout ? 'true' : 'false',
        },
      })
      throw reportedError
    }
  },
}

export default {
  namespaced: true,
  state: moduleState,
  getters,
  mutations,
  actions,
}
